import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, Transition as _Transition, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, TransitionGroup as _TransitionGroup, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-245a784e"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "flex gap-x4 items-start mt-x4 md:mt-x5" }
const _hoisted_2 = {
  key: 0,
  class: "flex flex-col justify-center"
}
const _hoisted_3 = ["disabled"]
const _hoisted_4 = {
  key: 0,
  class: "inline-block o-text-caption py-x1 px-12 font-bold border border-black max-w-600"
}
const _hoisted_5 = {
  key: 1,
  class: "text-roman"
}

import { computed, ref, Ref, watch } from 'vue';
import { Form } from 'vee-validate';
import utilities from '@/shared/utilities.service';
import ProductPurchaseOptions from '@/b2b/product/ui/ProductPurchaseOptions.vue';
import AddToBasketButtonContent from '@/shared/form/AddToBasketButtonContent.vue';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import useProductDetailData from '@/shared/product/details/useProductDetailData';
import useProductBasketButtonState from '@/shared/product/purchase/ui/useProductBasketButtonState';
import useProductDetailUpsell from '@/shared/product/upsell/useProductDetailUpsell';
import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import { hardRedirect } from '@/core/navigation/redirect.service';
import siteContextStore from '@/shared/content/site-context.store';
import WishlistButton from '@/shared/product/wishlist/ui/WishlistButton.vue';
import { PurchaseOptionType } from '@/types/productServerApi';
import useNetPrices from '@/b2b/product/useNetPrices';
import WebApiMessage from '@/core/http/WebApiMessage.vue';
import useProductAddOns from '@/shared/product/add-ons/useProductAddOns';
import basketTrackingService from '@/shared/tracking/basket-tracking.service';
import { useAddToBasket, webApiMessageId } from '@/b2b/product/purchase/useAddToBasket';
import QuantityField from '@/shared/checkout/basket/ui/QuantityField.vue';
import { debounce } from 'lodash';
import useTEMPB2BFeatureBasket from '@/b2b/basket/useTEMPB2BFeatureBasket';

export default /*#__PURE__*/_defineComponent({
  __name: 'ProductDetailPurchase',
  setup(__props) {

const editGuid = useRouteQuery(QueryKeys.Edit);
const labels = useBlockLabels();
const { basketButtonIsDisabled } = useProductBasketButtonState();
const { variantId, productName, productData, moduleConfiguratorTileData } = useProductDetailData();
const { maybeInitUpsellItems } = useProductDetailUpsell();
const { getIsNotInAssortment } = useNetPrices();
const { forceAddingState, addAddOnsToBasketSynchronous, addedAddOnsLabel, hasSelectedAddOns } = useProductAddOns();
const { addToBasket } = useAddToBasket();
const { lineItems, pickAndPackDeliveryMaximumQuantity, getIsPickAndPack, deleteItemBySku, replaceItem } = useTEMPB2BFeatureBasket();

const pending: Ref<boolean> = ref(false);
const timing: number = 2000;

const itemInBasket = computed(() => {
    // return basketStore.basket && basketStore.basket.lineItems?.find(item => item.sku === variantId.value)
    return lineItems.value.find(item => item.sku === variantId.value);
});
const quantity: Ref<number> = ref(itemInBasket.value?.quantity || 1);
const quantityActive: Ref<boolean> = ref(itemInBasket.value ?? false);
const isRemoving: Ref<boolean> = ref(false);
const selectedPurchaseOption: Ref<PurchaseOptionType | undefined> = ref();

const quantityMultiplier = computed(() => {
    if (itemInBasket.value && productData.value && getIsPickAndPack(itemInBasket.value) /* itemInBasket.value?.partnerExpressDeliverySelected */) {
        return productData?.value.purchasePossibilities?.find(x => x.type === PurchaseOptionType.B2BExpress)?.quantityMultiplier;
    }
    return productData.value.quantityMultiplier;
});

const syncToBasketQuantity = (item) => {
    quantityActive.value = !!item;
    quantity.value = item?.quantity ?? 1;
};

watch(itemInBasket, syncToBasketQuantity, { immediate: true });

const quantityMaxValue = computed(() => moduleConfiguratorTileData.value?.modules ? 1 : 9999999999);
const b2bExpressMaxQuantity = computed(() => pickAndPackDeliveryMaximumQuantity.value.toString() /* basketStore.basket.partnerExpressMaximumQuantity.toString() */);
const noAvailablePurchaseOptions = computed(() => productData.value.purchasePossibilities.length < 1);

const basketButtonAddedText = computed(() => {
    if (isRemoving.value) return labels.removedFromBasket;
    if (editGuid.value) return labels.addToBasketUpdated;
    if (addedAddOnsLabel.value) return addedAddOnsLabel.value;
    return labels.addedToBasket;
});

const basketButtonDefaultText = computed(() => {
    return basketButtonIsDisabled.value || noAvailablePurchaseOptions.value
        ? labels.notInStock
        : editGuid.value
            ? labels.addToBasketUpdate
            : itemInBasket.value ? labels.addToUpdateBasket : labels.addToBasket;
});

const submit = async({ purchaseOptionType }: { purchaseOptionType: PurchaseOptionType }, e?: Event, addOns = true): Promise<void> => {
    basketTrackingService.product(productData.value);
    if (!pending.value) {
        pending.value = true;
        const guid = editGuid.value;
        const animationTiming = utilities.delay(timing);
        try {
            addOns && forceAddingState();
            await addOrEdit(guid, purchaseOptionType);
            addOns && await handleAddOns(purchaseOptionType);
            if (!guid) {
                await maybeInitUpsellItems(purchaseOptionType);
            }
            await animationTiming;
            if (guid) {
                hardRedirect(siteContextStore.settings.basketPageUrl);
            }
        } catch (e) {
            itemInBasket.value && syncToBasketQuantity(itemInBasket.value);
        } finally {
            pending.value = false;
        }
    }
};

const removeFromBasket = async(): Promise<void> => {
    if (!pending.value) {
        pending.value = true;
        isRemoving.value = true;
        const animationTiming = utilities.delay(timing);
        try {
            await deleteItemBySku(variantId.value);
            await animationTiming;
            quantityActive.value = false;
            isRemoving.value = false;
            quantity.value = 1;
        } finally {
            pending.value = false;
        }
    }
};

const debouncedSubmit = debounce(submit, 1000);

const updatedQuantity = (qty) => {
    quantity.value = qty;
    if (selectedPurchaseOption.value) {
        debouncedSubmit({ purchaseOptionType: selectedPurchaseOption.value }, undefined, false);
    }
};

const onPurchaseOptionUpdate = (option: PurchaseOptionType) => {
    selectedPurchaseOption.value = option;
};

const handleAddOns = async(purchaseOptionType: PurchaseOptionType) => {
    if (hasSelectedAddOns.value) {
        await addAddOnsToBasketSynchronous(addToBasket, purchaseOptionType);
    };
};

const addOrEdit = async(guid: string, purchaseType: PurchaseOptionType): Promise<void> => {
    if (guid && purchaseType !== PurchaseOptionType.ModuleConfiguration) {
        await replaceItem(guid, variantId.value);
    } else if (guid && purchaseType === PurchaseOptionType.ModuleConfiguration) {
        await addToBasket(variantId.value, purchaseType);
        hardRedirect(siteContextStore.settings.basketPageUrl);
    } else {
        await addToBasket(variantId.value, purchaseType, quantity.value, false);
    }
};

const showAddToBasketButton = computed(() => {
    return !getIsNotInAssortment(variantId.value);
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Form), { onSubmit: submit }, {
    default: _withCtx(() => [
      _createVNode(ProductPurchaseOptions, { onUpdate: onPurchaseOptionUpdate }),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_Transition, {
          name: "o-anim-fade-300",
          mode: "out-in"
        }, {
          default: _withCtx(() => [
            (quantityActive.value && !_unref(editGuid))
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  (_unref(showAddToBasketButton) && !_unref(basketButtonIsDisabled) && !_unref(noAvailablePurchaseOptions))
                    ? (_openBlock(), _createBlock(QuantityField, {
                        key: 0,
                        "model-value": quantity.value,
                        multiplier: _unref(quantityMultiplier),
                        pending: pending.value,
                        max: _unref(quantityMaxValue),
                        class: _normalizeClass(["border border-dusty-grey px-15 py-6", {'opacity-40 pointer-events-none': pending.value}]),
                        "onUpdate:modelValue": updatedQuantity
                      }, null, 8, ["model-value", "multiplier", "pending", "max", "class"]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", {
                    class: _normalizeClass({'opacity-40 pointer-events-none': pending.value})
                  }, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "text-12 sm:text-14 text-dusty-grey c-magic-line c-magic-line--underline block mx-auto mt-5 whitespace-nowrap",
                      onClick: removeFromBasket
                    }, _toDisplayString(_unref(labels).removeFromBasket), 1)
                  ], 2)
                ]))
              : (_unref(showAddToBasketButton))
                ? (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    type: "submit",
                    disabled: _unref(basketButtonIsDisabled) || _unref(noAvailablePurchaseOptions),
                    class: "c-button c-button--primary min-w-220 transition-colors"
                  }, [
                    _createVNode(AddToBasketButtonContent, {
                      pending: pending.value,
                      "added-button-text": _unref(basketButtonAddedText),
                      "default-button-text": _unref(basketButtonDefaultText)
                    }, null, 8, ["pending", "added-button-text", "default-button-text"])
                  ], 8, _hoisted_3))
                : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        (!(_unref(moduleConfiguratorTileData) && _unref(moduleConfiguratorTileData).modules))
          ? (_openBlock(), _createBlock(WishlistButton, {
              key: 0,
              sku: _unref(variantId),
              name: _unref(productName),
              class: "mt-5 xl:mt-4"
            }, null, 8, ["sku", "name"]))
          : _createCommentVNode("", true)
      ]),
      _createVNode(WebApiMessage, {
        id: _unref(webApiMessageId),
        "clear-timing": 8000,
        class: "mt-x2"
      }, {
        default: _withCtx(({ messages }) => [
          _createVNode(_TransitionGroup, {
            name: "o-anim-fade-300",
            class: "list-reset",
            tag: "ul"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(messages, (message, ix) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: message+ix
                }, [
                  (message.value === 'PartnerExpressDeliveryOverLimit')
                    ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(_ctx.$dictionary.format(_ctx.$dictionary.get('b2b/product/partnerExpressDeliveryOverLimit'), [_unref(b2bExpressMaxQuantity)])), 1))
                    : (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(message.value || _ctx.$dictionary.get('global/generic/unexpectedServerError')), 1))
                ]))
              }), 128))
            ]),
            _: 2
          }, 1024)
        ]),
        _: 1
      }, 8, ["id"])
    ]),
    _: 1
  }))
}
}

})